import { PointKey } from '@/components/AnnotationMetadata';
import { useExplore } from '@/stores/explore';
import { EPSG, toProjected } from '@skand/data-3d-loader';
import { lengthToString, toCartographic, toDegrees } from '@skand/viewer-component-v2';
import { useCallback, useEffect, useState } from 'react';
import { Vector3 } from 'three';
import { Item } from './Item';

interface PointProps {
  position: Vector3;
}

interface PointData {
  east: number;
  north: number;
  elevation: number;
  latitude: number;
  longitude: number;
}

export const Point = ({ position }: PointProps) => {
  const epsg = useExplore(state => state.epsg);
  const measurementUnit = useExplore(state => state.measurementUnit);

  const [point, setPoint] = useState<PointData>({
    east: 0,
    north: 0,
    elevation: 0,
    latitude: 0,
    longitude: 0,
  });

  const eastingString = useCallback(
    (point: PointData['east'], pointKey: PointKey) => {
      let value;
      let label;

      if (epsg === 'EPSG:4978') {
        value = lengthToString(point, measurementUnit, 5);
        label = 'X';
      } else if (epsg === 'EPSG:4326') {
        value = point.toFixed(5) + 'º';
        label = 'Longitude';
      } else {
        value = lengthToString(point, measurementUnit, 5);
        label = 'Easting';
      }

      return pointKey === PointKey.Label ? label : value;
    },
    [epsg, measurementUnit],
  );

  const northingString = useCallback(
    (point: PointData['north'], pointKey: PointKey) => {
      let value;
      let label;

      if (epsg === 'EPSG:4978') {
        value = lengthToString(point, measurementUnit, 5);
        label = 'Y';
      } else if (epsg === 'EPSG:4326') {
        value = point.toFixed(5) + 'º';
        label = 'Latitude';
      } else {
        value = lengthToString(point, measurementUnit, 5);
        label = 'Northing';
      }

      return pointKey === PointKey.Label ? label : value;
    },
    [epsg, measurementUnit],
  );

  const elevationString = useCallback(
    (point: PointData['elevation'], pointKey: PointKey) => {
      let value;
      let label;

      if (epsg === 'EPSG:4978') {
        value = lengthToString(point, measurementUnit, 2);
        label = 'Z';
      } else {
        value = lengthToString(point, measurementUnit, 2);
        label = 'Elevation';
      }
      return pointKey === PointKey.Label ? label : value;
    },
    [epsg, measurementUnit],
  );

  // Asyncronously compute point data
  useEffect(() => {
    const fn = async () => {
      const epsgTokens = epsg.split('-');
      const coordinates = (
        await toProjected([position], epsgTokens[0] as EPSG, epsgTokens.length > 1)
      )[0];
      const cartographic = toCartographic(position);
      return {
        east: coordinates.x,
        north: coordinates.y,
        elevation: coordinates.z,
        latitude: toDegrees(cartographic.latitude),
        longitude: toDegrees(cartographic.longitude),
      };
    };
    fn().then(setPoint);
  }, [epsg, measurementUnit, position]);

  return (
    <div>
      <Item
        label={eastingString(point.east, PointKey.Label)}
        value={eastingString(point.east, PointKey.Value)}
      />
      <Item
        label={northingString(point.north, PointKey.Label)}
        value={northingString(point.north, PointKey.Value)}
      />
      <Item
        label={elevationString(point.elevation, PointKey.Label)}
        value={elevationString(point.elevation, PointKey.Value)}
      />
      <Item
        label="Latitude/Longitude"
        value={`${point.latitude.toFixed(6)}/${point.longitude.toFixed(6)}`}
      />
    </div>
  );
};
